import * as coam from '@cimpress-technology/coam-sapidus'
import { locations } from '@cimpress-technology/logistics-configuration-client'
import * as React from 'react'
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic'
import { useTranslation } from 'react-i18next'
import { Route, Switch, useParams, useRouteMatch } from 'react-router'
import { v4 } from 'uuid'
import { useAppContext } from '../common/AppContext'
import { bearerToken } from '../common/auth'
import DevelopmentFragment from '../common/components/DevelopmentFragment'
import Preloader from '../common/components/Preloader'
import FourOhFourPage from '../common/FourOhFourPage'
import FragmentLoader from '../common/FragmentLoader'
import * as models from '../common/models'
import * as locationsStore from '../common/proxy/locations-store'
import NetworkPermissionsEditor from '../permissions/components/NetworkPermissionsEditor'
import NetworkDetails from './components/NetworkDetails'
import NetworkPackingFragment from './components/NetworkPackingFragment'
import { NetworkContext, Provider } from './NetworkContext'

export default function ViewNetworkPage() {
  const { containerType } = useAppContext()
  const { t } = useTranslation()
  const [loading, setLoading] = React.useState(true)
  const [network, setNetwork] = React.useState<models.Network>()
  const [networkLocations, setNetworkLocations] = React.useState<
    locations.models.LocationWithLinks[]
  >([])
  const params = useParams<{ networkId: string }>()
  const match = useRouteMatch()

  const loadNetwork = async (networkId: string) => {
    setLoading(true)

    const fetchedNetwork = await locationsStore.getNetwork(networkId)

    if (!fetchedNetwork) {
      return
    }

    const allLocations = await locationsStore.getMultipleLocations(
      fetchedNetwork.apiNetwork.logisticsLocationIds
    )

    setNetworkLocations(allLocations)
    setNetwork(fetchedNetwork)
    setLoading(false)
  }

  React.useEffect(() => {
    const fetchData = async () => {
      await loadNetwork(params.networkId)
    }
    fetchData()
  }, [params.networkId])

  if (loading) {
    return <Preloader />
  }

  if (!network) {
    return <FourOhFourPage />
  }

  const reloadNetwork = async () => {
    await loadNetwork(params.networkId)
  }

  const updateNetwork = async (
    change:
      | locations.models.LogisticsNetworkWithLinks
      | ((n: locations.models.LogisticsNetworkWithLinks) => void)
  ): Promise<void> => {
    await locationsStore.updateNetwork(network.apiNetwork, change)
    await reloadNetwork()
  }

  const linkResourceToNetwork = (
    resourceId: string,
    resourceType: coam.models.ResourceTypes
  ) => {
    return locations.linkResourceToNetworkGroup(
      bearerToken(),
      v4(),
      network.apiNetwork.id,
      resourceId,
      resourceType
    )
  }

  const context: NetworkContext = {
    network,
    updateNetwork,
    linkResourceToNetwork,
    locations: networkLocations,
  }

  return (
    <Provider value={context}>
      <Switch>
        <Route path={`${match.url}/calendars`}>
          <FragmentLoader src="https://calendars-ui.qp.cimpress.io/fragment.js" />
        </Route>
        <Route path={`${match.path}/user-management`}>
          <main className={`App-content flex-vertical ${containerType}`}>
            <BreadcrumbsItem to="/">
              {t('common.logisticsLocations')}
            </BreadcrumbsItem>
            <NetworkPermissionsEditor />
          </main>
        </Route>
        <Route path={`${match.url}/packing`}>
          <main className={`App-content flex-vertical ${containerType}`}>
            <NetworkPackingFragment />
          </main>
        </Route>
        <Route path={`${match.url}/fragments`}>
          <DevelopmentFragment />
        </Route>
        <Route path={`${match.path}`}>
          <main className={`App-content flex-vertical ${containerType}`}>
            <NetworkDetails />
          </main>
        </Route>
      </Switch>
    </Provider>
  )
}
